
// ============= CORE TYPES =============
/**
 * Result of AES-GCM encryption
 */
export interface AesGcmEncryptionResult {
    /**
     * The encrypted data (ciphertext without auth tag)
     */
    encryptedData: Uint8Array;

    /**
     * Initialization Vector (IV) used for encryption
     */
    iv: Uint8Array;

    /**
     * Authentication tag for verifying integrity
     */
    authTag: Uint8Array;
  }

export interface MLKEMKeyPair {
  privateKey: Uint8Array;
  publicKey: Uint8Array;
}

export interface EncapsulationResult {
  cipherText: Uint8Array;
  sharedSecret: Uint8Array;
}

export interface DecapsulationResult {
  cipherText: Uint8Array;
  sharedSecret: Uint8Array;
}

export interface RecipientKeyData {
  id: string;
  publicKey: Uint8Array;
}

export interface EncryptedRecipientKey {
  recipientId: string;
  cipherText: Uint8Array;
  encryptedFileKey: AesGcmEncryptionResult;
}

export interface EncryptedComponents {
  data: Uint8Array;
  iv: Uint8Array;
  authTag: Uint8Array;
}

export interface PasswordHashResult {
  sha512Hash: string;
  keyHex: string;
  encoded: string;
  hash: Uint8Array;
}

export interface HashResult {
  hash: Uint8Array;
  hexString: string;
}

// ============= CRYPTO SERVICE INTERFACE =============

/**
 * Core cryptographic operations service.
 * Handles all cryptographic primitives with consistent, purpose-focused naming.
 */
export abstract class CryptoService {

  // ============= KEY GENERATION =============

  /**
   * Generate AES-256 encryption key as hex string
   */
  abstract generateAesKey(): string;

  /**
   * Generate ML-KEM key pair for post-quantum encryption
   * @param seed Optional 32-byte seed for deterministic key generation
   */
  abstract generateMlKemKeyPair(seed?: Uint8Array): Promise<MLKEMKeyPair>;

  /**
   * Generate a deterministic ML-KEM key pair from email and password
   * Uses Argon2id to derive a consistent seed, ensuring the same email+password
   * always produces the same keypair
   * @param email User's email address
   * @param password User's password
   * @returns ML-KEM key pair
   */
  abstract generateMlKemKeyPairFromCredentials(
    email: string,
    password: string
  ): Promise<MLKEMKeyPair>;

  // ============= KEY ENCAPSULATION =============
  abstract encryptPrivateKey(privateKey: Uint8Array, ukek: {
    sha512Hash: string;
    keyHex: string;
    encoded: string;
    hash: Uint8Array;
  }): Promise<string>;

  /**
   * Decrypt a private key that was encrypted with encryptPrivateKey
   * @param encryptedPrivateKeyHex The encrypted private key as hex string (IV + encrypted data)
   * @param ukekHash The UKEK hash used for decryption
   * @returns The decrypted private key
   */
  abstract decryptPrivateKey(encryptedPrivateKeyHex: string, ukekHash: Uint8Array): Promise<Uint8Array>;
  /**
   * Encapsulate a shared secret using ML-KEM
   * @param publicKey The recipient's ML-KEM public key
   * @param seed Optional deterministic seed
   * @returns Encapsulation result with cipher text and shared secret
   */
  abstract encapsulateMlKem(
    publicKey: Uint8Array,
    seed?: Uint8Array
  ): EncapsulationResult;

  /**
   * Decapsulate a shared secret using ML-KEM
   * @param cipherText The cipher text to decapsulate
   * @param privateKey The ML-KEM private key for decapsulation
   * @returns Decapsulation result with cipher text and shared secret
   */
  abstract decapsulateMlKem(
    cipherText: Uint8Array,
    privateKey: Uint8Array
  ): DecapsulationResult;

  // ============= SYMMETRIC ENCRYPTION =============

  /**
   * Encrypt data using AES-GCM
   * @param data The data to encrypt
   * @param key The encryption key (must be 256 bits)
   * @returns Encryption result with data, IV, and auth tag
   */
  abstract encrypt(
    data: Uint8Array,
    key: Uint8Array
  ): Promise<AesGcmEncryptionResult>;

  /**
   * Decrypt data using AES-GCM
   * @param encryptedData The encrypted data
   * @param iv The initialization vector
   * @param authTag The authentication tag
   * @param key The decryption key
   * @returns The decrypted data
   */
  abstract decrypt(
    encryptedData: Uint8Array,
    iv: Uint8Array,
    authTag: Uint8Array,
    key: Uint8Array
  ): Promise<Uint8Array>;

  // ============= HASHING =============

  /**
   * Calculate SHA-256 hash of data
   * @param data The data to hash (supports multiple input types)
   * @returns The hash as Uint8Array
   */
  abstract hashSha256(
    data: File | Blob | ArrayBuffer | Uint8Array
  ): Promise<Uint8Array>;

  /**
   * Calculate SHA-512 hash of string data
   * @param input The string to hash
   * @returns The hash and hex representation
   */
  abstract hashSha512(input: string): Promise<HashResult>;

  // ============= PASSWORD OPERATIONS =============

  /**
   * Hash password for secure storage using Argon2id
   * @param password The password to hash
   * @param emailForSalt Email used for salt generation
   * @returns Complete password hash result
   */
  abstract hashPassword(
    password: string,
    emailForSalt: string
  ): Promise<PasswordHashResult>;

  /**
   * Derive encryption key from password using Argon2id
   * @param password The user's password
   * @param email Email used for salt generation
   * @returns Derived key as hex string
   */
  abstract deriveKey(
    password: string,
    email: string
  ): Promise<string>;

  // ============= UTILITIES =============

  /**
   * Generate cryptographically secure random bytes
   * @param length The number of bytes to generate
   * @returns Random bytes as Uint8Array
   */
  abstract randomBytes(length: number): Uint8Array;
}
